import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { TextMetrics } from "pixi.js";

import { PixiSettings } from "../../../../../../pixiSettings";

// Device multipliers for spine scale and vertical offset (used in getDeviceMultipliers)
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = {
  scale: 1.35,
  offset: 0.5,
};

const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = {
  scale: 1.25,
  offset: 0.6,
};

const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = {
  scale: 1.8,
  offset: 0.75,
};

const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = {
  scale: 1.8,
  offset: 0.8,
};

const DEVICE_MULTIPLIERS_DESKTOP = {
  scale: 1.25,
  offset: 0.6,
};

/**
 * IntroPopupController - Handles intro popup spine animation
 */
class IntroPopupController {
  constructor(container, dimensions, uiSymbolSize, dimensionsContext, t) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = uiSymbolSize;
    this.symbolHeight = uiSymbolSize;
    this.dimensionsContext = dimensionsContext;
    this.t = t; // Translation function

    // Spine instance
    this.introSpine = null;
    this.isPlaying = false;
    this.initialized = false;

    // Cache for device multipliers
    this._deviceMultipliersCache = null;
    // Cache for animation name
    this._animationNameCache = null;
    // Cache for available animations
    this._availableAnimationsCache = null;

    // Text containers attached to spine slots (position/scale/rotation auto-updated by Spine)
    this.maxWinContainer = null;
    this.maxWinText = null;
    this.tapToStartContainer = null;
    this.tapToStartText = null;
    this.basegameContainer = null;
    this.basegameText = null;
    this.hnsContainer = null;
    this.hnsText = null;
    this.cardContainers = [];

    this.initializeIntroSpine();
  }

  /**
   * Proportional scaling based on symbol size with device-specific multipliers
   */
  applyScaleAndCenter() {
    if (!this.introSpine || !this.dimensions || !this.initialized) return;

    // Additional safety check - ensure spine is still valid
    if (!this.introSpine.scale || !this.introSpine.parent) return;

    // Use uiSymbolScale from dimensionsContext (= uiSymbolHeight / GRID_SYMBOL_HEIGHT)
    let scale = this.dimensionsContext?.uiSymbolScale || 1;
    scale = Math.max(0.1, Math.min(scale, 2));

    // Get device/orientation-specific multipliers
    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    try {
      this.introSpine.scale.set(scale);

      // Apply device/orientation-specific offset multiplier
      const baseOffset = this.symbolHeight || 0;

      this.introSpine.x = this.dimensions.width / 2;
      this.introSpine.y =
        this.dimensions.height / 2 - baseOffset * deviceOffsetMultiplier;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Get device-specific multipliers for scale and offset
   * Returns an object with scale and offset multipliers
   * Result is cached and invalidated when device/orientation changes
   */
  getDeviceMultipliers() {
    if (this._deviceMultipliersCache !== null) {
      return this._deviceMultipliersCache;
    }

    if (!this.dimensionsContext) {
      this._deviceMultipliersCache = { scale: 1.0, offset: 1.0 };
      return this._deviceMultipliersCache;
    }

    const { device, orientation } = this.dimensionsContext;

    let result;
    if (device === PixiSettings.DEVICE.MOBILE) {
      result =
        orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? DEVICE_MULTIPLIERS_MOBILE_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      result =
        orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? DEVICE_MULTIPLIERS_TABLET_PORTRAIT
          : DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
    } else {
      result = DEVICE_MULTIPLIERS_DESKTOP;
    }

    this._deviceMultipliersCache = result;
    return result;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   * Also checks for device/orientation changes and switches animation if needed
   */
  updateDimensions(dimensions, uiSymbolSize, dimensionsContext) {
    // Don't update if spine has been cleaned up
    if (!this.initialized || !this.introSpine) return;

    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensions?.width ||
      this.dimensions.height !== dimensions?.height;

    const symbolWidthChanged = this.symbolWidth !== uiSymbolSize;
    const symbolHeightChanged = this.symbolHeight !== uiSymbolSize;

    // Check if device/orientation changed
    const deviceChanged =
      !this.dimensionsContext ||
      this.dimensionsContext.device !== dimensionsContext?.device ||
      this.dimensionsContext.orientation !== dimensionsContext?.orientation;

    if (
      !dimensionsChanged &&
      !symbolWidthChanged &&
      !symbolHeightChanged &&
      !deviceChanged
    )
      return;

    // Store previous animation name to detect changes
    const previousAnimationName = this.getAnimationName();

    // Invalidate caches if device/orientation changed
    if (deviceChanged) {
      this._deviceMultipliersCache = null;
      this._animationNameCache = null;
    }

    this.dimensions = dimensions;
    this.symbolWidth = uiSymbolSize;
    this.symbolHeight = uiSymbolSize;
    if (dimensionsContext) {
      this.dimensionsContext = dimensionsContext;
    }

    if (
      this.introSpine &&
      this.initialized &&
      (dimensionsChanged || symbolWidthChanged || symbolHeightChanged)
    ) {
      this.applyScaleAndCenter();
    }

    // If device/orientation changed, switch animation instantly
    if (deviceChanged && this.introSpine && this.initialized) {
      const newAnimationName = this.getAnimationName();
      if (previousAnimationName !== newAnimationName) {
        this.playAnimation(true); // Force re-play with new animation
      }
    }
  }

  /**
   * Initialize the intro spine
   */
  initializeIntroSpine() {
    try {
      this.introSpine = this.createIntroSpine();

      if (!this.introSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.introSpine.skeleton) missingComponents.push("skeleton");
      if (!this.introSpine.state) missingComponents.push("state");
      if (!this.introSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.introSpine);
      this.introSpine.alpha = 1;
      this.introSpine.zIndex = 1;

      // Invalidate animations cache when spine is recreated
      this._availableAnimationsCache = null;

      // Set initialized before applying scale so applyScaleAndCenter can run
      this.initialized = true;

      // Apply scale and center positioning - this needs initialized to be true
      this.applyScaleAndCenter();

      if (this.introSpine.parent !== this.container) {
        return;
      }

      // Initialize text elements attached to slots (Spine auto-updates transform each frame)
      this.initializeMaxWinText();
      this.initializeTapToStartText();
      this.initializeFeatureTexts();

      this.playAnimation();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Create intro spine from loaded assets
   */
  createIntroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "intro_screen-skeleton";
      const atlasAlias = "intro_screen-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

      const introSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!introSpine?.skeleton?.data?.animations) return null;

      return introSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animations from the spine
   * Result is cached to avoid repeated array mapping
   */
  getAvailableAnimations() {
    // Return cached result if available
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    const animations =
      this.introSpine?.skeleton?.data?.animations.map((a) => a.name) || [];
    this._availableAnimationsCache = animations;
    return animations;
  }

  /**
   * Get the correct animation name based on device/orientation
   * Mobile portrait -> intro_portrait, everything else -> intro_landscape
   * Result is cached and invalidated when device/orientation changes
   */
  getAnimationName() {
    // Return cached result if available
    if (this._animationNameCache !== null) {
      return this._animationNameCache;
    }

    if (!this.dimensionsContext) {
      this._animationNameCache = "intro_landscape";
      return this._animationNameCache;
    }

    const { device, orientation } = this.dimensionsContext;

    let result;
    // Mobile portrait uses intro_portrait, everything else uses intro_landscape
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT
    ) {
      result = "intro_portrait";
    } else if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      // Portrait mode on any device should use portrait animation
      result = "intro_portrait";
    } else {
      result = "intro_landscape";
    }

    this._animationNameCache = result;
    return result;
  }

  /**
   * Start playing the intro animation based on device/orientation
   */
  playAnimation(force = false) {
    if ((this.isPlaying && !force) || !this.introSpine?.state) return;

    try {
      this.isPlaying = true;
      this.introSpine.alpha = 1;
      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
      this.introSpine.state.apply(this.introSpine.skeleton);
      this.introSpine.skeleton.setToSetupPose();

      const animations = this.getAvailableAnimations();
      const targetAnimationName = this.getAnimationName();

      const targetAnimation = animations.find(
        (anim) => anim === targetAnimationName,
      );

      if (targetAnimation) {
        // Smooth crossfade on loop restart (prevents text slot jump)
        this.introSpine.state.data.defaultMix = 0.1;
        // Play the appropriate animation in a loop
        this.introSpine.state.setAnimation(0, targetAnimation, true);
      } else {
        // Fallback to first available animation
        const firstAnim = animations[0];
        if (firstAnim) {
          this.introSpine.state.setAnimation(0, firstAnim, true);
        }
      }
    } catch (error) {
      this.isPlaying = false;
    }
  }

  /**
   * Stop the animation and hide the spine
   */
  stopAnimation() {
    if (!this.introSpine || !this.initialized) return;

    this.isPlaying = false;
    this.introSpine.alpha = 0.001;
    if (this.introSpine.state) {
      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
    }
  }

  /**
   * Initialize max win text and attach it to spine's "txt_slot_maxwin" slot
   */
  initializeMaxWinText() {
    if (!this.introSpine || !this.introSpine.skeleton || !this.t) {
      return;
    }

    const maxWinSlot = this.introSpine.skeleton.findSlot("txt_slot_maxwin");
    if (!maxWinSlot) {
      return;
    }

    // Create container and attach to slot (Spine auto-updates position/rotation/scale)
    this.maxWinContainer = new PIXI.Container();
    this.maxWinContainer.name = "max_win_text_attached_to_slot";
    this.maxWinContainer.alpha = 1;
    this.maxWinContainer.zIndex = 1000;

    // Get translated text with replaced variable
    const MAX_WIN_MULTIPLIER = 5000;
    const maxWinText = this.t("start_page_max_win").replace(
      "{MAX_MULTIPLIER}",
      MAX_WIN_MULTIPLIER.toLocaleString(),
    );

    // Define max dimensions - narrower width and max 3 rows
    const maxWidth = 500; // Narrower to prevent expansion
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const idealFontSize = slotText.fontSize;
    const MAX_ROWS = 3;

    let finalFontSize = idealFontSize;

    // Check if text fits within constraints
    const tempStyle = new PIXI.TextStyle({
      fontFamily: PixiSettings.FONT_STYLES.PRIMARY.fontFamily,
      fontSize: idealFontSize,
      fontWeight: slotText.fontWeight,
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxWidth,
    });

    const metrics = TextMetrics.measureText(maxWinText, tempStyle);
    const lineHeight = metrics.lineHeight || idealFontSize * 1.2;
    const numberOfRows = Math.ceil(metrics.height / lineHeight);

    // Only scale down if text exceeds 3 rows
    if (numberOfRows > MAX_ROWS) {
      for (let i = 0; i < 20; i++) {
        const testStyle = new PIXI.TextStyle({
          fontFamily: PixiSettings.FONT_STYLES.PRIMARY.fontFamily,
          fontSize: finalFontSize,
          fontWeight: slotText.fontWeight,
          align: "center",
          wordWrap: true,
          wordWrapWidth: maxWidth,
        });

        const testMetrics = TextMetrics.measureText(maxWinText, testStyle);
        const testLineHeight = testMetrics.lineHeight || finalFontSize * 1.2;
        const testRows = Math.ceil(testMetrics.height / testLineHeight);

        if (testRows <= MAX_ROWS) {
          break;
        }

        const rowScale = MAX_ROWS / testRows;
        const scale = rowScale * 0.95;
        finalFontSize = Math.round(finalFontSize * scale);

        if (finalFontSize < 12) {
          finalFontSize = 12;
          break;
        }
      }
    }

    // Single-layer text using centralized PRIMARY + SLOT_TEXT font styles
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const textStyle = new PIXI.TextStyle({
      ...primaryStyle,
      ...slotText,
      fontSize: finalFontSize,
      wordWrap: true,
      wordWrapWidth: maxWidth,
    });

    this.maxWinText = new PIXI.Text(maxWinText, textStyle);
    this.maxWinText.resolution = slotText.resolution;
    this.maxWinText.anchor.set(0.5);
    this.maxWinText.x = 0;
    this.maxWinText.y = 0;

    this.maxWinContainer.addChild(this.maxWinText);
    this.introSpine.addSlotObject("txt_slot_maxwin", this.maxWinContainer);
  }

  /**
   * Initialize tap to start text and attach it to spine's "txt_slot_click_to" slot
   */
  initializeTapToStartText() {
    if (!this.introSpine || !this.introSpine.skeleton || !this.t) {
      return;
    }

    const tapToStartSlot =
      this.introSpine.skeleton.findSlot("txt_slot_click_to");
    if (!tapToStartSlot) {
      return;
    }

    // Create container and attach to slot (Spine auto-updates position/rotation/scale)
    this.tapToStartContainer = new PIXI.Container();
    this.tapToStartContainer.name = "tap_to_start_text_attached_to_slot";
    this.tapToStartContainer.alpha = 1;
    this.tapToStartContainer.zIndex = 1001;

    // Get translated text
    const tapToStartText = this.t("tap_to_start").toUpperCase();

    // Define max dimensions for the text area
    const maxWidth = 800;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const idealFontSize = slotText.fontSize;

    // Create temp style to measure text
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const tempStyle = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      fontSize: idealFontSize,
      fontWeight: slotText.fontWeight,
      align: "center",
    });

    // Measure text to check if it fits
    const metrics = TextMetrics.measureText(tapToStartText, tempStyle);

    // Calculate final font size - scale down if text is too wide
    let finalFontSize = idealFontSize;
    if (metrics.width > maxWidth) {
      const scale = maxWidth / metrics.width;
      finalFontSize = Math.round(idealFontSize * scale);
    }

    // Single-layer text using centralized PRIMARY + SLOT_TEXT font styles
    const textStyle = new PIXI.TextStyle({
      ...primaryStyle,
      ...slotText,
      fontSize: finalFontSize,
    });

    this.tapToStartText = new PIXI.Text(tapToStartText, textStyle);
    this.tapToStartText.resolution = slotText.resolution;
    this.tapToStartText.anchor.set(0.5);
    this.tapToStartText.x = 0;
    this.tapToStartText.y = 0;

    this.tapToStartContainer.addChild(this.tapToStartText);
    this.introSpine.addSlotObject(
      "txt_slot_click_to",
      this.tapToStartContainer,
    );
  }

  /**
   * Calculate optimal font size for text to fit within constraints
   * @param {string} text - The text to measure
   * @param {number} maxWidth - Maximum width in pixels
   * @param {number} idealFontSize - Starting font size
   * @param {number} maxRows - Maximum number of rows allowed
   * @returns {number} - Calculated font size
   */
  calculateFontSize(text, maxWidth, idealFontSize, maxRows) {
    let finalFontSize = idealFontSize;

    // Iteratively scale down if text exceeds max rows (max 20 iterations)
    for (let i = 0; i < 20; i++) {
      const tempStyle = new PIXI.TextStyle({
        fontFamily: PixiSettings.FONT_STYLES.PRIMARY.fontFamily,
        fontSize: finalFontSize,
        fontWeight: PixiSettings.FONT_STYLES.SLOT_TEXT.fontWeight,
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxWidth,
      });

      const metrics = TextMetrics.measureText(text, tempStyle);
      const lineHeight = metrics.lineHeight || finalFontSize * 1.2;
      const numberOfRows = Math.ceil(metrics.height / lineHeight);

      // Check if text fits within row limit
      if (numberOfRows <= maxRows) {
        break;
      }

      // Scale down to fit within max rows
      const rowScale = maxRows / numberOfRows;
      const scale = rowScale * 0.95; // Safety margin
      finalFontSize = Math.round(finalFontSize * scale);

      // Prevent font from becoming too small
      if (finalFontSize < 12) {
        finalFontSize = 12;
        break;
      }
    }

    return finalFontSize;
  }

  /**
   * Initialize feature description texts and attach them to spine's txt_slot_feature_1/2 slots
   */
  initializeFeatureTexts() {
    if (!this.introSpine || !this.introSpine.skeleton) {
      return;
    }

    const FEATURES = [
      {
        slot: "txt_slot_feature_1",
        text: "FEATURE DESCRIPTION\nPLACEHOLDER TEXT",
      },
      {
        slot: "txt_slot_feature_2",
        text: "FEATURE DESCRIPTION\nPLACEHOLDER TEXT",
      },
    ];

    const maxWidth = 610;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const idealFontSize = slotText.fontSize;
    const MAX_ROWS = 4;

    // Calculate font sizes for all features and use the smallest for consistency
    const fontSizes = FEATURES.map((f) =>
      this.calculateFontSize(f.text, maxWidth, idealFontSize, MAX_ROWS),
    );
    const finalFontSize = Math.min(...fontSizes);

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const textStyle = new PIXI.TextStyle({
      ...primaryStyle,
      ...slotText,
      fontSize: finalFontSize,
      lineHeight: finalFontSize * 1.2 - 2,
      wordWrap: true,
      wordWrapWidth: maxWidth,
    });

    this.cardContainers = [];

    for (const feature of FEATURES) {
      const slot = this.introSpine.skeleton.findSlot(feature.slot);
      if (!slot) continue;

      const container = new PIXI.Container();
      container.name = `${feature.slot}_text`;
      container.alpha = 1;
      container.zIndex = 999;

      const text = new PIXI.Text(feature.text, textStyle);
      text.resolution = slotText.resolution;
      text.anchor.set(0.5);
      text.x = 0;
      text.y = 10;

      container.addChild(text);
      this.introSpine.addSlotObject(feature.slot, container);
      this.cardContainers.push(container);
    }
  }

  /**
   * Cleanup resources - completely destroy spine and reset state
   */
  cleanup() {
    // Stop animation first
    this.isPlaying = false;

    // Remove slot objects from spine (detaches containers from slot tracking)
    if (this.introSpine?.removeSlotObjects) {
      this.introSpine.removeSlotObjects();
    }

    // Cleanup text containers - destroy containers with children, no need to destroy text separately
    if (this.maxWinContainer) {
      this.maxWinContainer.destroy({ children: true });
      this.maxWinContainer = null;
      this.maxWinText = null;
    }

    if (this.tapToStartContainer) {
      this.tapToStartContainer.destroy({ children: true });
      this.tapToStartContainer = null;
      this.tapToStartText = null;
    }

    if (this.basegameContainer) {
      this.basegameContainer.destroy({ children: true });
      this.basegameContainer = null;
      this.basegameText = null;
    }

    if (this.hnsContainer) {
      this.hnsContainer.destroy({ children: true });
      this.hnsContainer = null;
      this.hnsText = null;
    }
    // Cleanup card containers
    for (const container of this.cardContainers) {
      container.destroy({ children: true });
    }
    this.cardContainers = [];

    if (this.introSpine) {
      try {
        // Clear all animation state
        if (this.introSpine.state) {
          this.introSpine.state.clearListeners();
          this.introSpine.state.clearTracks();
          this.introSpine.state.timeScale = 1.0;
        }

        // Remove from container
        if (this.container?.children.includes(this.introSpine)) {
          this.container.removeChild(this.introSpine);
        }

        // Destroy the spine completely
        this.introSpine.destroy();
        this.introSpine = null;
      } catch (error) {
        // Force set to null even if destroy fails
        this.introSpine = null;
      }
    }

    // Reset all state flags
    this.initialized = false;
    this.isPlaying = false;

    // Clear all caches
    this._deviceMultipliersCache = null;
    this._animationNameCache = null;
    this._availableAnimationsCache = null;
  }
}

export default IntroPopupController;
